import { ReactNode, useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { ChatPanel, ChatToggleButton } from '@/features/chat-panel'

interface MainLayoutProps {
  children: ReactNode
}

// Storage key for sidebar state
const SIDEBAR_COLLAPSED_KEY = 'admin_sidebar_collapsed'

export function MainLayout({ children }: MainLayoutProps) {
  const location = useLocation()
  const [isChatOpen, setIsChatOpen] = useState(false)

  // Extract agentType from URL path like /agents/:agentType
  const currentAgentType = useMemo(() => {
    const match = location.pathname.match(/^\/agents\/([^/]+)/)
    return match ? match[1] : undefined
  }, [location.pathname])
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
    return saved === 'true'
  })

  // Persist sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed))
  }, [sidebarCollapsed])

  // Don't show layout on login page
  if (location.pathname === '/login') {
    return <>{children}</>
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header />

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>

      {/* Floating Chat Panel */}
      <ChatToggleButton isOpen={isChatOpen} onClick={() => setIsChatOpen(true)} />
      <ChatPanel isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} defaultAgentType={currentAgentType} />
    </div>
  )
}
